import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { ScopeMode } from 'types/channel';
import { listChannels, ListChannelsResponse, subscribeSphereToChannel } from '../../../api/marketplaceApi';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import './AddChannelDialogOrganism.scss';

export interface AddChannelDialogOrganismProps {
  open: boolean;
  sphereId: string;
  organizationId: string;
  onClose: () => void;
  onSubscribed: () => void;
}

type ChannelEntry = ListChannelsResponse['channels'][number];

const AddChannelDialogOrganism: React.FC<AddChannelDialogOrganismProps> = ({
  open,
  sphereId,
  organizationId,
  onClose,
  onSubscribed,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const [searchTerm, setSearchTerm] = useState('');
  const [channels, setChannels] = useState<ChannelEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [scopeMode, setScopeMode] = useState<ScopeMode>('direct');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);

  const fetchChannels = useCallback(async () => {
    if (!apiClients?.schemaClient) return;

    setLoading(true);
    try {
      const result = await listChannels(apiClients.schemaClient, {
        status: 'approved',
        limit: 50,
      });
      setChannels(result.channels);
    } catch {
      setChannels([]);
    } finally {
      setLoading(false);
    }
  }, [apiClients, organizationId]);

  useEffect(() => {
    if (open) {
      fetchChannels();
      setSearchTerm('');
      setSelectedChannelId(null);
      setScopeMode('direct');
      setError(null);
      setSubmitting(false);
    }
  }, [open, fetchChannels]);

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open, loading]);

  const filteredChannels = channels.filter((ch) => {
    if (!searchTerm.trim()) return true;
    const name = getLocalizedText(ch.name).toLowerCase();
    const desc = getLocalizedText(ch.description).toLowerCase();
    const term = searchTerm.toLowerCase();
    return name.includes(term) || desc.includes(term);
  });

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!apiClients?.schemaClient || !selectedChannelId) return;

      setSubmitting(true);
      setError(null);

      try {
        await subscribeSphereToChannel(
          apiClients.schemaClient,
          selectedChannelId,
          sphereId,
          scopeMode
        );
        onSubscribed();
        onClose();
      } catch (err: unknown) {
        if (err instanceof Error && err.message.includes('409')) {
          setError(t('sphereChannels.addChannel.errorDuplicate'));
        } else {
          setError(t('sphereChannels.addChannel.error'));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [apiClients, selectedChannelId, sphereId, scopeMode, organizationId, onSubscribed, onClose, t]
  );

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [submitting, onClose]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  if (!open) return null;

  return (
    <div
      className="marketplace-dialog-overlay"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
    >
      <div
        className="marketplace-dialog marketplace-add-channel-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-channel-dialog-title"
      >
        <div className="marketplace-dialog__header">
          <h2 id="add-channel-dialog-title" className="marketplace-dialog__title">
            {t('sphereChannels.addChannel.title')}
          </h2>
          <button
            type="button"
            className="marketplace-dialog__close-btn"
            onClick={handleClose}
            aria-label={t('sphereChannels.addChannel.cancel')}
          >
            &times;
          </button>
        </div>

        <form className="marketplace-dialog__body" onSubmit={handleSubmit}>
          <div className="marketplace-add-channel-dialog__search">
            <i className="pi pi-search marketplace-add-channel-dialog__search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              className="marketplace-dialog__input marketplace-add-channel-dialog__search-input"
              placeholder={t('sphereChannels.addChannel.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="marketplace-add-channel-dialog__channel-list">
            {loading && (
              <div className="marketplace-add-channel-dialog__loading">
                <i className="pi pi-spin pi-spinner" />
              </div>
            )}

            {!loading && filteredChannels.length === 0 && (
              <div className="marketplace-add-channel-dialog__empty">
                {t('sphereChannels.addChannel.noResults')}
              </div>
            )}

            {!loading &&
              filteredChannels.map((ch) => (
                <button
                  key={ch.id}
                  type="button"
                  className={`marketplace-add-channel-dialog__channel-item ${
                    selectedChannelId === ch.id
                      ? 'marketplace-add-channel-dialog__channel-item--selected'
                      : ''
                  }`}
                  onClick={() => setSelectedChannelId(ch.id)}
                >
                  <div className="marketplace-add-channel-dialog__channel-icon">
                    <i className="pi pi-megaphone" />
                  </div>
                  <div className="marketplace-add-channel-dialog__channel-info">
                    <span className="marketplace-add-channel-dialog__channel-name">
                      {getLocalizedText(ch.name)}
                    </span>
                    {ch.description && getLocalizedText(ch.description) && (
                      <span className="marketplace-add-channel-dialog__channel-desc">
                        {getLocalizedText(ch.description)}
                      </span>
                    )}
                  </div>
                </button>
              ))}
          </div>

          <div className="marketplace-add-channel-dialog__scope-section">
            <label className="marketplace-dialog__label">
              {t('sphereChannels.scopeMode.label')}
            </label>
            <div className="marketplace-add-channel-dialog__scope-options">
              <button
                type="button"
                className={`marketplace-add-channel-dialog__scope-option ${
                  scopeMode === 'direct'
                    ? 'marketplace-add-channel-dialog__scope-option--selected'
                    : ''
                }`}
                onClick={() => setScopeMode('direct')}
              >
                <i className="pi pi-circle" />
                <span>{t('sphereChannels.scopeMode.direct')}</span>
              </button>
              <button
                type="button"
                className={`marketplace-add-channel-dialog__scope-option ${
                  scopeMode === 'recursive'
                    ? 'marketplace-add-channel-dialog__scope-option--selected'
                    : ''
                }`}
                onClick={() => setScopeMode('recursive')}
              >
                <i className="pi pi-sitemap" />
                <span>{t('sphereChannels.scopeMode.recursive')}</span>
              </button>
            </div>
          </div>

          {error && (
            <div className="marketplace-dialog__error">
              {error}
            </div>
          )}

          <div className="marketplace-dialog__actions">
            <button
              type="button"
              className="marketplace-dialog__cancel-btn"
              onClick={handleClose}
              disabled={submitting}
            >
              {t('sphereChannels.addChannel.cancel')}
            </button>
            <button
              type="submit"
              className="marketplace-dialog__submit-btn"
              disabled={!selectedChannelId || submitting}
            >
              {submitting
                ? t('sphereChannels.addChannel.subscribing')
                : t('sphereChannels.addChannel.confirm')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddChannelDialogOrganism;
